import React from 'react';
import classnames from 'classnames/bind';
import styles from './PreviewView.module.scss';
import { Button } from '@components';
import ReplayIcon from '@mui/icons-material/Replay';
import { Icon, Input, Loader, Typography } from 'gd-design-library';
import { PlacementGenerateResponse } from '@services';

const cx = classnames.bind(styles);

const CN = 'preview-view';
const actionButtonsClassName = `${CN}__action-buttons`;

interface PreviewViewProps {
  placementResult: PlacementGenerateResponse;
  videoBase64: string | null;
  videoError: string | null;
  notes: string;
  setNotes: (notes: string) => void;
  editPlacement: () => Promise<void>;
  isEditingImage: boolean;
  resetPlacement: (keepResult: boolean) => void;
}

export const PreviewView: React.FC<PreviewViewProps> = ({
  placementResult,
  videoBase64,
  videoError,
  notes,
  setNotes,
  editPlacement,
  isEditingImage,
  resetPlacement
}) => (
  <div className={ cx(CN) }>
    <Typography>
      {placementResult.responseText}
    </Typography>
    {placementResult.imageBase64 && (
      <img
        src={ placementResult.imageBase64.startsWith('data:')
          ? placementResult.imageBase64
          : `data:image/png;base64,${placementResult.imageBase64}`
        }
        alt='Placement result'
      />
    )}
    {placementResult.videoOperationName ? (
      <>
        {videoBase64 ? (
          <video
            controls
            autoPlay
            muted
            loop
            src={ videoBase64.startsWith('data:')
              ? videoBase64
              : `data:video/mp4;base64,${videoBase64}`
            }
            style={ { maxWidth: '100%' } }
          >
            Your browser does not support the video tag.
          </video>
        ) : videoError ? (
          <Typography color='error'>
            Video generation failed: {videoError}
          </Typography>
        ) : (
          <>
            <Loader
              name='dots'
              size='sm'
              variant='inline'
              withWrapper={ false }
              style={ { alignSelf: 'center', marginTop: '1rem' } }
            />
            <Typography style={ { alignSelf: 'center' } }>
              Generating video...
            </Typography>
          </>
        )}
      </>
    ) : null}
    {!placementResult.videoOperationName && (
      <Input
        placeholder='Edit notes'
        value={ notes }
        disabled={ isEditingImage }
        onChange={ (event: React.ChangeEvent<HTMLInputElement>): void => {
          setNotes(event.target.value);
        } }
        onKeyDown={ (event: React.KeyboardEvent<HTMLInputElement>): void => {
          if (event.key === 'Enter' && !isEditingImage && !placementResult.videoOperationName) {
            editPlacement();
          }
        } }
      />
    )}
    <div className={ cx(actionButtonsClassName) }>
      <Button
        variant='outlined'
        iconStart={ <ReplayIcon fontSize='small' /> }
        onClick={ (): void => resetPlacement(false) }
      >
        Start over
      </Button>
      {!placementResult.videoOperationName && (
        <Button
          variant='primary'
          iconStart={ <Icon name='edit' width={ 20 } height={ 20 } /> }
          onClick={ editPlacement }
          loading={ isEditingImage }
          disabled={ !notes.trim() }
        >
          Edit image
        </Button>
      )}
    </div>
  </div>
);
